import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import {
  fmtMoney, balanceClass, MONTHS, thisMonth, todayStr, shiftMonth, buildShiftMaps, ABSENT_STYLE,
} from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CaretLeft, CaretRight, DownloadSimple, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { EmployeeDetail } from "@/components/EmployeeDetail";

const NAME_W = 150, GENDER_W = 46, OPEN_W = 84;

export default function Attendance() {
  const [view, setView] = useState("day");
  const [shifts, setShifts] = useState([]);
  const maps = useMemo(() => buildShiftMaps(shifts), [shifts]);
  const [detailId, setDetailId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => { api.get("/shifts").then((r) => setShifts(r.data)); }, []);

  return (
    <div className="p-4 md:p-8 max-w-[1600px] mx-auto w-full space-y-5">
      <PageHeader title="Attendance" subtitle="Mark the shift worked and any lend given, per day.">
        <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
          {["day", "month"].map((v) => (
            <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
              className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                view === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}>{v} view</button>
          ))}
        </div>
      </PageHeader>

      {view === "day"
        ? <DayView shifts={shifts} maps={maps} onOpenDetail={setDetailId} reloadKey={reloadKey} />
        : <MonthView shifts={shifts} maps={maps} onOpenDetail={setDetailId} reloadKey={reloadKey} />}

      <EmployeeDetail empId={detailId} onClose={() => setDetailId(null)} onChanged={() => setReloadKey((k) => k + 1)} />
    </div>
  );
}

/* =============== DAY VIEW (mobile-first) =============== */
function DayView({ shifts, maps, onOpenDetail, reloadKey }) {
  const [date, setDate] = useState(todayStr());
  const [employees, setEmployees] = useState([]);
  const [att, setAtt] = useState({});
  const [buckets, setBuckets] = useState({});
  const [defaultBucket, setDefaultBucket] = useState("regular");
  const [lends, setLends] = useState({});
  const [balances, setBalances] = useState({});
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkShift, setBulkShift] = useState("");
  const [bulkBucket, setBulkBucket] = useState("");

  const load = useCallback(async () => {
    const [e, d, b] = await Promise.all([
      api.get("/employees"),
      api.get(`/attendance/day?date=${date}`),
      api.get(`/balances?as_of=${date}`),
    ]);
    setEmployees(e.data);
    setAtt(d.data.attendance);
    setBuckets(d.data.buckets);
    setDefaultBucket(d.data.default_bucket);
    setLends(d.data.lends);
    setBalances(b.data);
  }, [date, reloadKey]);
  useEffect(() => { load(); }, [load]);

  const setShift = async (eid, sid) => {
    const value = att[eid] === sid ? null : sid;
    await api.post("/attendance", { employee_id: eid, date, shift_id: value });
    load();
  };
  const setBucket = async (eid, bucket) => {
    const sid = att[eid];
    if (!sid || sid === "A") return;
    await api.post("/attendance", { employee_id: eid, date, shift_id: sid, bucket });
    load();
  };
  const saveLend = async (eid, amount) => {
    const bucket = buckets[eid] || defaultBucket;
    await api.post("/lends", { employee_id: eid, date, amount: Number(amount) || 0, bucket });
    load();
  };

  const toggleSel = (eid) => {
    const n = new Set(selected);
    n.has(eid) ? n.delete(eid) : n.add(eid);
    setSelected(n);
  };
  const applyBulk = async () => {
    if (selected.size === 0) { toast.error("Select at least one labourer"); return; }
    if (!bulkShift) { toast.error("Choose a shift to apply"); return; }
    const payload = { employee_ids: [...selected], date, shift_id: bulkShift };
    if (bulkShift !== "A" && bulkBucket) payload.bucket = bulkBucket;
    await api.post("/attendance/bulk", payload);
    toast.success(`Applied to ${selected.size} labourer(s)`);
    setSelected(new Set()); setBulkMode(false); setBulkShift(""); setBulkBucket(""); load();
  };

  const label = new Date(date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="day-prev" onClick={() => setDate(new Date(new Date(date).getTime() - 86400000).toISOString().slice(0, 10))}
          className="h-10 w-10 flex items-center justify-center rounded-md border border-slate-300 bg-white hover:bg-slate-50"><CaretLeft size={16} weight="bold" /></button>
        <Input data-testid="day-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 font-data h-10" />
        <button data-testid="day-next" onClick={() => setDate(new Date(new Date(date).getTime() + 86400000).toISOString().slice(0, 10))}
          className="h-10 w-10 flex items-center justify-center rounded-md border border-slate-300 bg-white hover:bg-slate-50"><CaretRight size={16} weight="bold" /></button>
        <span className="font-data text-sm text-slate-500 ml-1 hidden sm:inline">{label}</span>
        {defaultBucket === "arecanut" && (
          <span data-testid="season-badge" className="text-[10px] uppercase tracking-wider font-semibold bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
            Arecanut season
          </span>
        )}
        <Button data-testid="bulk-toggle" variant={bulkMode ? "default" : "outline"} onClick={() => { setBulkMode(!bulkMode); setSelected(new Set()); }}
          className={`ml-auto gap-2 ${bulkMode ? "bg-slate-900" : ""}`}>
          <Lightning size={16} weight="bold" /> Bulk fill
        </Button>
      </div>

      {bulkMode && (
        <div className="flex flex-wrap items-center gap-2 bg-slate-100 border border-slate-200 rounded-md p-3" data-testid="bulk-bar">
          <span className="text-sm font-medium text-slate-600">{selected.size} selected · set to</span>
          <Select value={bulkShift} onValueChange={setBulkShift}>
            <SelectTrigger className="w-44 bg-white" data-testid="bulk-shift"><SelectValue placeholder="Choose shift" /></SelectTrigger>
            <SelectContent>
              {shifts.map((s) => <SelectItem key={s.id} value={s.id}>{maps.label[s.id]} · {s.name}</SelectItem>)}
              <SelectItem value="A">Absent</SelectItem>
            </SelectContent>
          </Select>
          {bulkShift && bulkShift !== "A" && (
            <Select value={bulkBucket} onValueChange={setBulkBucket}>
              <SelectTrigger className="w-36 bg-white" data-testid="bulk-bucket"><SelectValue placeholder={`Default (${defaultBucket})`} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="regular">Regular</SelectItem>
                <SelectItem value="arecanut">Arecanut</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button data-testid="bulk-apply" onClick={applyBulk} className="bg-slate-900">Apply</Button>
          <button onClick={() => setSelected(new Set(employees.map((e) => e.id)))} className="text-sm text-slate-600 underline">Select all</button>
        </div>
      )}

      {employees.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center">
          <p className="text-slate-600 font-medium">No labourers yet.</p>
          <p className="text-sm text-slate-500 mt-1">Add labourers from the "Labourers" tab first.</p>
        </div>
      )}

      <div className="space-y-2.5">
        {employees.map((e) => {
          const cur = att[e.id];
          const isWorkDay = cur && cur !== "A";
          const bucket = buckets[e.id] || defaultBucket;
          const bal = balances[e.id] || {};
          return (
            <div key={e.id} data-testid={`day-row-${e.id}`}
              className="bg-white border border-slate-200 rounded-lg shadow-sm p-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-3 sm:w-52 flex-shrink-0">
                {bulkMode && (
                  <Checkbox data-testid={`bulk-check-${e.id}`} checked={selected.has(e.id)} onCheckedChange={() => toggleSel(e.id)} />
                )}
                <div>
                  <button data-testid={`open-employee-${e.id}`} onClick={() => onOpenDetail(e.id)}
                    className="font-medium text-slate-800 hover:text-slate-900 hover:underline text-left">
                    {e.name}
                  </button>
                  <div className="text-xs text-slate-400 font-data">{e.gender === "M" ? "Male" : "Female"}</div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-1.5 flex-1">
                {shifts.map((s) => (
                  <button key={s.id} data-testid={`day-shift-${e.id}-${s.id}`} onClick={() => setShift(e.id, s.id)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                      cur === s.id ? maps.style[s.id] + " ring-2 ring-slate-900 ring-offset-1" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                    }`}>
                    {maps.label[s.id]}
                  </button>
                ))}
                <button data-testid={`day-shift-${e.id}-A`} onClick={() => setShift(e.id, "A")}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                    cur === "A" ? ABSENT_STYLE + " ring-2 ring-slate-900 ring-offset-1" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                  }`}>Absent</button>
                {isWorkDay && (
                  <div className="inline-flex rounded border border-slate-300 overflow-hidden ml-1" data-testid={`day-bucket-${e.id}`}>
                    <button onClick={() => setBucket(e.id, "regular")}
                      className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                        bucket === "regular" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"
                      }`}>Regular</button>
                    <button onClick={() => setBucket(e.id, "arecanut")}
                      className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                        bucket === "arecanut" ? "bg-emerald-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"
                      }`}>Arecanut</button>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4 sm:w-72 flex-shrink-0 justify-between sm:justify-end">
                <div className="relative w-28">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-data">Lend ₹</span>
                  <Input data-testid={`day-lend-${e.id}`} type="number" defaultValue={lends[e.id] || ""} key={`${e.id}-${lends[e.id] || 0}`}
                    onBlur={(ev) => saveLend(e.id, ev.target.value)} className="pl-14 h-9 font-data text-right" placeholder="0" />
                  <div className={`absolute -bottom-4 right-0 text-[9px] uppercase tracking-wider ${bucket === "arecanut" ? "text-emerald-600" : "text-slate-400"}`}>
                    → {bucket === "arecanut" ? "Arecanut" : "Regular"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Balance</div>
                  <div className={`font-data text-sm ${balanceClass(bal.regular)}`} data-testid={`day-balance-${e.id}`}>{fmtMoney(bal.regular)}</div>
                  {!!bal.arecanut && (
                    <div className={`font-data text-[11px] ${balanceClass(bal.arecanut)}`} data-testid={`day-balance-arecanut-${e.id}`}>AC {fmtMoney(bal.arecanut)}</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =============== MONTH GRID (desktop) =============== */
function MonthCell({ value, bucket, shifts, maps, onSet, onSetBucket, testid }) {
  const isWorkDay = value && value !== "A";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={testid}
          className={`relative h-8 w-full min-w-[34px] font-data text-xs font-semibold border transition-colors focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-inset ${
            value === "A" ? ABSENT_STYLE : value ? maps.style[value] : "bg-transparent text-slate-300 hover:bg-slate-100"
          }`}>
          {value === "A" ? "A" : value ? maps.label[value] : "·"}
          {isWorkDay && bucket === "arecanut" && (
            <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-emerald-600" title="Arecanut" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="min-w-[170px]">
        {shifts.map((s) => (
          <DropdownMenuItem key={s.id} data-testid={`${testid}-opt-${s.id}`} onClick={() => onSet(s.id)}>
            {maps.label[s.id]} · {s.name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem data-testid={`${testid}-opt-A`} onClick={() => onSet("A")}>Absent</DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testid}-opt-clear`} onClick={() => onSet(null)}>Clear</DropdownMenuItem>
        {isWorkDay && (
          <>
            <DropdownMenuItem data-testid={`${testid}-opt-regular`} disabled={bucket !== "arecanut"} onClick={() => onSetBucket("regular")}>
              Mark Regular
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`${testid}-opt-arecanut`} disabled={bucket === "arecanut"} onClick={() => onSetBucket("arecanut")}>
              Mark Arecanut
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MonthView({ shifts, maps, onOpenDetail, reloadKey }) {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/attendance/month?month=${month}`).then((r) => setData(r.data));
  }, [month, reloadKey]);
  useEffect(() => { load(); }, [load]);

  const days = data ? Array.from({ length: data.days_in_month }, (_, i) => i + 1) : [];
  const setCell = async (eid, day, sid) => {
    const d = `${month}-${String(day).padStart(2, "0")}`;
    await api.post("/attendance", { employee_id: eid, date: d, shift_id: sid });
    load();
  };
  const setCellBucket = async (eid, day, sid, bucket) => {
    const d = `${month}-${String(day).padStart(2, "0")}`;
    await api.post("/attendance", { employee_id: eid, date: d, shift_id: sid, bucket });
    load();
  };
  const setLendCell = async (eid, day, amount, bucket) => {
    const d = `${month}-${String(day).padStart(2, "0")}`;
    const payload = { employee_id: eid, date: d, amount: Number(amount) || 0 };
    if (bucket) payload.bucket = bucket;
    await api.post("/lends", payload);
    load();
  };
  const label = `${MONTHS[parseInt(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
  const summaryCols = [...shifts.map((s) => maps.label[s.id]), "Abs", "Earned", "Lend", "Prev", "Paid", "Balance",
    "AC Earned", "AC Advance", "AC Lend", "AC Balance"];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-white border border-slate-300 rounded-md">
          <button data-testid="month-prev" onClick={() => setMonth(shiftMonth(month, -1))} className="h-9 px-2 hover:bg-slate-100 rounded-l-md"><CaretLeft size={16} weight="bold" /></button>
          <span data-testid="month-label" className="font-data text-sm font-medium px-2 min-w-[120px] text-center">{label}</span>
          <button data-testid="month-next" onClick={() => setMonth(shiftMonth(month, 1))} className="h-9 px-2 hover:bg-slate-100 rounded-r-md"><CaretRight size={16} weight="bold" /></button>
        </div>
        <Button data-testid="btn-export-excel" className="bg-slate-900 hover:bg-slate-800 gap-2 ml-auto"
          onClick={() => downloadFile(`/export/excel?month=${month}`, `attendance_${month}.xlsx`)}>
          <DownloadSimple size={16} weight="bold" /> Export Excel
        </Button>
      </div>

      {data && data.rows.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500">No labourers to show.</div>
      )}

      {data && data.rows.length > 0 && (
        <div className="matrix-scroll relative w-full rounded-md border border-slate-200 bg-white shadow-sm overflow-auto max-h-[72vh]">
          <table className="border-collapse text-sm whitespace-nowrap w-full" data-testid="attendance-matrix">
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 bg-slate-100 border-b border-r border-slate-300 px-3 py-2 text-left text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ minWidth: NAME_W, left: 0 }}>Name</th>
                <th className="sticky top-0 z-30 bg-slate-100 border-b border-r border-slate-300 px-1 py-2 text-center text-xs font-semibold uppercase text-slate-500" style={{ minWidth: GENDER_W, left: NAME_W }}>Sx</th>
                <th className="sticky top-0 z-30 bg-slate-100 border-b border-r border-slate-300 px-2 py-2 text-right text-xs font-semibold uppercase text-slate-500" style={{ minWidth: OPEN_W, left: NAME_W + GENDER_W }}>Open</th>
                {days.map((d) => (
                  <th key={d} className="sticky top-0 z-20 bg-slate-100 border-b border-r border-slate-200 px-1 py-2 text-center text-xs font-semibold text-slate-500 font-data" style={{ minWidth: 34 }}>{d}</th>
                ))}
                {summaryCols.map((h) => (
                  <th key={h} className="sticky top-0 z-20 bg-slate-200 border-b border-r border-slate-300 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-600" style={{ minWidth: 70 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <Fragment key={r.employee_id}>
                  <tr className="group">
                    <td rowSpan={2} className="sticky left-0 z-10 bg-white border-b-2 border-r border-slate-200 px-3 py-1 align-middle" style={{ minWidth: NAME_W, left: 0 }}>
                      <button data-testid={`open-employee-${r.employee_id}`} onClick={() => onOpenDetail(r.employee_id)}
                        className="font-medium text-slate-800 hover:text-slate-900 hover:underline text-left">
                        {r.name}
                      </button>
                    </td>
                    <td rowSpan={2} className="sticky z-10 bg-white border-b-2 border-r border-slate-200 px-1 py-1 text-center font-data text-xs text-slate-500 align-middle" style={{ minWidth: GENDER_W, left: NAME_W }}>{r.gender}</td>
                    <td rowSpan={2} className="sticky z-10 bg-white border-b-2 border-r border-slate-200 px-2 py-1 text-right font-data text-xs text-slate-600 align-middle" style={{ minWidth: OPEN_W, left: NAME_W + GENDER_W }}>{fmtMoney(r.opening_balance)}</td>
                    {days.map((d) => (
                      <td key={d} className="border-b border-r border-slate-100 p-0 text-center" style={{ minWidth: 34 }}>
                        <MonthCell value={r.days[d] || null} bucket={r.day_buckets[d]} shifts={shifts} maps={maps}
                          onSet={(sid) => setCell(r.employee_id, d, sid)}
                          onSetBucket={(bucket) => setCellBucket(r.employee_id, d, r.days[d], bucket)}
                          testid={`cell-${r.employee_id}-${d}`} />
                      </td>
                    ))}
                    {shifts.map((s) => (
                      <td key={s.id} rowSpan={2} className="border-b-2 border-r border-slate-200 px-2 py-1 text-center font-data text-xs align-middle">{r.shift_counts[s.id] || 0}</td>
                    ))}
                    <td rowSpan={2} className="border-b-2 border-r border-slate-200 px-2 py-1 text-center font-data text-xs text-rose-600 align-middle">{r.absent}</td>
                    <td rowSpan={2} className="border-b-2 border-r border-slate-200 px-2 py-1 text-right font-data text-xs align-middle">{fmtMoney(r.month_earned)}</td>
                    <td rowSpan={2} className="border-b-2 border-r border-slate-200 px-2 py-1 text-right font-data text-xs text-orange-600 align-middle">{fmtMoney(r.month_lend)}</td>
                    <td rowSpan={2} className={`border-b-2 border-r border-slate-200 px-2 py-1 text-right font-data text-xs align-middle ${balanceClass(r.previous_balance)}`}>{fmtMoney(r.previous_balance)}</td>
                    <td rowSpan={2} className="border-b-2 border-r border-slate-200 px-2 py-1 text-right font-data text-xs align-middle">{fmtMoney(r.month_paid)}</td>
                    <td rowSpan={2} className={`border-b-2 border-r border-slate-300 px-2 py-1 text-right font-data text-xs bg-slate-50 align-middle ${balanceClass(r.balance)}`} data-testid={`balance-${r.employee_id}`}>{fmtMoney(r.balance)}</td>
                    <td rowSpan={2} className="border-b-2 border-r border-slate-200 px-2 py-1 text-right font-data text-xs text-emerald-700 align-middle">{fmtMoney(r.arecanut_earned)}</td>
                    <td rowSpan={2} className="border-b-2 border-r border-slate-200 px-2 py-1 text-right font-data text-xs text-emerald-700 align-middle">{fmtMoney(r.arecanut_advance)}</td>
                    <td rowSpan={2} className="border-b-2 border-r border-slate-200 px-2 py-1 text-right font-data text-xs text-emerald-700 align-middle">{fmtMoney(r.arecanut_lend)}</td>
                    <td rowSpan={2} className={`border-b-2 border-r border-slate-300 px-2 py-1 text-right font-data text-xs bg-emerald-50 align-middle ${balanceClass(r.arecanut_balance)}`} data-testid={`arecanut-balance-${r.employee_id}`}>{fmtMoney(r.arecanut_balance)}</td>
                  </tr>
                  <tr key={`${r.employee_id}-lend`} className="bg-orange-50/30">
                    {days.map((d) => (
                      <td key={d} className="border-b-2 border-r border-slate-100 p-0" style={{ minWidth: 34 }}>
                        <input data-testid={`lend-${r.employee_id}-${d}`} type="number" defaultValue={r.lends[d] || ""} key={`${d}-${r.lends[d] || 0}`}
                          onBlur={(e) => setLendCell(r.employee_id, d, e.target.value, r.day_buckets[d])}
                          className="h-7 w-full min-w-[34px] text-center font-data text-[10px] bg-transparent text-orange-700 focus:outline-none focus:bg-orange-100" placeholder="·" />
                      </td>
                    ))}
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-slate-400">Each labourer has two rows: top = shift worked, bottom (orange) = lend given that day — it settles against whichever bucket that day is marked (Regular or Arecanut). A <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-600 align-middle" /> dot marks a day counted against the Arecanut balance instead of Regular — click a day to change it.</p>
    </div>
  );
}
